import { useMemo } from 'react';
import { Vec3 } from '@/core/math/Vec3';
import { Game } from '@/game/Game';
import { CelestialBody } from '@/universe/CelestialBody';

/**
 * Orbital map of the system.
 *
 * Every curve on this map is sampled from the same propagator the simulation
 * uses, by asking each body where it will be at a series of times across one of
 * its own periods. Nothing here re-derives an ellipse from elements, so the map
 * cannot disagree with the world: if an orbit is drawn wrong, the orbit *is*
 * wrong.
 *
 * The radial axis is logarithmic. A system spanning 0.3 to 30 AU drawn linearly
 * puts every inner planet inside one pixel of the star, which is accurate and
 * useless.
 */

const SIZE = 620;
const CENTRE = SIZE / 2;
const MARGIN = 26;

interface Plotted {
  body: CelestialBody;
  x: number;
  y: number;
  orbit: string;
  colour: string;
  radius: number;
}

function bodyColour(body: CelestialBody): string {
  if (body.def.kind === 'star') return '#ffd591';
  if (body.def.kind === 'moon') return '#9aa7b4';
  const p = body.def.palette.midland;
  const to255 = (v: number): number => Math.round(Math.min(1, Math.max(0, Math.sqrt(v))) * 255);
  return `rgb(${to255(p[0])}, ${to255(p[1])}, ${to255(p[2])})`;
}

export function SystemMap({
  game,
  selected,
  onSelect,
  focus,
}: {
  game: Game;
  selected: string | null;
  onSelect: (id: string) => void;
  /** Body whose children are shown. Null shows everything orbiting the star. */
  focus: string | null;
}): JSX.Element {
  const plot = useMemo(() => {
    const t = game.time;
    const centreBody = focus ? game.system.find(focus) : null;
    const origin = centreBody ? game.system.positionOf(centreBody, t) : new Vec3();

    const shown = game.system.bodies.filter((b) => {
      if (b.def.kind === 'star') return !centreBody;
      return centreBody ? b.parent?.id === centreBody.id : b.parent?.def.kind === 'star';
    });

    // Distances in the map plane. The orbital reference plane has +Y as its
    // normal, so the map is the XZ plane seen from the north.
    const flat = (v: Vec3): { u: number; w: number } => ({ u: v.x - origin.x, w: v.z - origin.z });

    let maxDistance = 1;
    const samples = new Map<string, { u: number; w: number }[]>();
    for (const body of shown) {
      const period = body.orbitalPeriod;
      if (!Number.isFinite(period) || period <= 0) {
        samples.set(body.id, []);
        continue;
      }
      const points: { u: number; w: number }[] = [];
      const steps = 160;
      for (let i = 0; i <= steps; i++) {
        const p = flat(game.system.positionOf(body, t + (period * i) / steps));
        points.push(p);
        maxDistance = Math.max(maxDistance, Math.hypot(p.u, p.w));
      }
      samples.set(body.id, points);
    }

    // Logarithmic radial mapping, anchored so the innermost orbit is not
    // squashed into the star.
    const usable = CENTRE - MARGIN;
    const knee = maxDistance / 400;
    const project = (u: number, w: number): [number, number] => {
      const d = Math.hypot(u, w);
      if (d <= 0) return [CENTRE, CENTRE];
      const scaled = (Math.log1p(d / knee) / Math.log1p(maxDistance / knee)) * usable;
      return [CENTRE + (u / d) * scaled, CENTRE + (w / d) * scaled];
    };

    const plotted: Plotted[] = shown.map((body) => {
      const here = flat(game.system.positionOf(body, t));
      const [x, y] = project(here.u, here.w);
      const points = samples.get(body.id) ?? [];
      const orbit = points
        .map((p, i) => {
          const [px, py] = project(p.u, p.w);
          return `${i === 0 ? 'M' : 'L'}${px.toFixed(1)} ${py.toFixed(1)}`;
        })
        .join(' ');
      return {
        body,
        x,
        y,
        orbit,
        colour: bodyColour(body),
        radius: body.def.kind === 'star' ? 9 : body.def.kind === 'moon' ? 3.5 : 5.5,
      };
    });

    // The vessel, in the same projection.
    const vesselRoot = game.system.frames.transformPosition(
      game.ship.body.position,
      game.ship.referenceBody.inertialFrameId,
      game.system.root,
      t,
    );
    const vesselFlat = flat(vesselRoot);
    const [vx, vy] = project(vesselFlat.u, vesselFlat.w);

    return { plotted, vessel: { x: vx, y: vy }, centreBody, maxDistance };
  }, [game, focus, selected]);

  return (
    <svg viewBox={`0 0 ${SIZE} ${SIZE}`} className="system-map" role="img" aria-label="System map">
      <defs>
        <radialGradient id="starGlow">
          <stop offset="0%" stopColor="rgba(255, 220, 150, 0.55)" />
          <stop offset="100%" stopColor="rgba(255, 220, 150, 0)" />
        </radialGradient>
      </defs>

      {!plot.centreBody && <circle cx={CENTRE} cy={CENTRE} r={46} fill="url(#starGlow)" />}

      {plot.centreBody && (
        <>
          <circle cx={CENTRE} cy={CENTRE} r={7} fill={bodyColour(plot.centreBody)} />
          <text x={CENTRE + 12} y={CENTRE + 4} className="map-label">
            {plot.centreBody.name}
          </text>
        </>
      )}

      {plot.plotted.map((p) => (
        <path
          key={`orbit-${p.body.id}`}
          d={p.orbit}
          fill="none"
          stroke={p.body.id === selected ? p.colour : 'rgba(140, 170, 200, 0.28)'}
          strokeWidth={p.body.id === selected ? 1.4 : 0.8}
        />
      ))}

      <g>
        <circle cx={plot.vessel.x} cy={plot.vessel.y} r={5.5} fill="none" stroke="#7ee0ff" strokeWidth={1.2} />
        <line
          x1={plot.vessel.x - 9}
          y1={plot.vessel.y}
          x2={plot.vessel.x + 9}
          y2={plot.vessel.y}
          stroke="#7ee0ff"
          strokeWidth={0.9}
        />
        <line
          x1={plot.vessel.x}
          y1={plot.vessel.y - 9}
          x2={plot.vessel.x}
          y2={plot.vessel.y + 9}
          stroke="#7ee0ff"
          strokeWidth={0.9}
        />
      </g>

      {plot.plotted.map((p) => (
        <g
          key={p.body.id}
          onClick={() => onSelect(p.body.id)}
          style={{ cursor: 'pointer' }}
          className={p.body.id === selected ? 'map-body selected' : 'map-body'}
        >
          <circle cx={p.x} cy={p.y} r={p.radius + 7} fill="transparent" />
          <circle cx={p.x} cy={p.y} r={p.radius} fill={p.colour} />
          {p.body.id === selected && (
            <circle cx={p.x} cy={p.y} r={p.radius + 5} fill="none" stroke={p.colour} strokeWidth={1} />
          )}
          <text x={p.x + p.radius + 5} y={p.y + 4} className="map-label">
            {p.body.name}
          </text>
        </g>
      ))}
    </svg>
  );
}
